import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// Create context with undefined default
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check if we're in a test environment
const isCypress = typeof window !== 'undefined' && (window as any).Cypress;

// Ensure AuthProvider is declared as a React function component
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we're in Cypress and have mock auth state available, use it
    if (isCypress && (window as any).mockAuthState) {
      const mockState = (window as any).mockAuthState;
      setUser(mockState.user);
      setProfile(mockState.profile);
      setLoading(mockState.loading);
      return;
    }

    // Normal authentication flow for non-test environments
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId: string) {
    try {
      // First try to get the existing profile
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // If no profile exists, create one
      if (!data && !error) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user?.email) {
          throw new Error('User email not available');
        }

        ({ data, error } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: userData.user.email,
            role: 'customer'
          })
          .select()
          .single());

        if (error) {
          // If insert failed due to race condition, try fetching again
          if (error.code === '23505') {
            ({ data, error } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .single());
          } else {
            throw error;
          }
        }
      }

      if (error) {
        throw error;
      }

      setProfile(data);
    } catch (error) {
      console.error('Error in profile management:', error);
      setProfile(null);
    }
  }

  // Use mock functions in test environment
  async function signIn(email: string, password: string) {
    if (isCypress && (window as any).mockAuthState?.signIn) {
      return (window as any).mockAuthState.signIn(email, password);
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.status === 400) {
        throw new Error('Invalid email or password');
      }
      throw error;
    }
  }

  async function signUp(email: string, password: string) {
    if (isCypress && (window as any).mockAuthState?.signUp) {
      return (window as any).mockAuthState.signUp(email, password);
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
  }

  async function signOut() {
    if (isCypress && (window as any).mockAuthState?.signOut) {
      return (window as any).mockAuthState.signOut();
    }

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        // Still clear the local state to "sign out" locally
        setUser(null);
        setProfile(null);
      }
    } catch (err) {
      console.error('Exception during sign out:', err);
      // Still clear the local state to "sign out" locally
      setUser(null);
      setProfile(null);
    }
  }

  // Memoize the context value to prevent unnecessary re-renders
  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthProvider };
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}